import { AccountingDashboardRevenueGrowthDailyRow } from '@/renderer/types/accounting';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildSalesGrowthChartData,
  formatCompactCurrency,
  formatCurrencyValue,
} from './accountingDashboard';

interface AccountingSalesGrowthCardProps {
  rows: AccountingDashboardRevenueGrowthDailyRow[];
}

export function AccountingSalesGrowthCard({
  rows,
}: AccountingSalesGrowthCardProps) {
  const data = buildSalesGrowthChartData(rows);
  const totalRevenue = rows.reduce(
    (sum, row) => sum + row.total_revenue_amount,
    0,
  );
  const latestRevenue = rows[rows.length - 1]?.total_revenue_amount || 0;
  const resolveSeriesLabel = (seriesName: string) => {
    switch (seriesName.toLowerCase()) {
      case 'revenue':
        return 'Total';
      case 'pos':
        return 'POS';
      case 'accommodation':
        return 'Accommodation';
      default:
        return seriesName;
    }
  };

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-950">Sales growth</h3>
          <p className="mt-1 text-sm text-slate-500">
            Combined revenue trend from POS settlements and accommodation stays.
          </p>
        </div>
        <div className="rounded-2xl bg-slate-50 px-4 py-3">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
            Revenue in range
          </p>
          <p className="mt-1 text-xl font-semibold text-slate-950">
            {formatCompactCurrency(totalRevenue)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Latest daily total {formatCurrencyValue(latestRevenue)}
          </p>
        </div>
      </div>

      <div className="mt-5 h-[320px]">
        {!data.length ? (
          <div className="flex h-full items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
            No revenue trend is available for this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 8, right: 16, left: -10, bottom: 0 }}
            >
              <CartesianGrid vertical={false} stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                tickFormatter={(value) => formatCompactCurrency(Number(value))}
              />
              <Tooltip
                formatter={(value, name) => [
                  formatCurrencyValue(Number(value ?? 0)),
                  resolveSeriesLabel(String(name)),
                ]}
                contentStyle={{
                  borderRadius: 16,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#2563eb"
                strokeWidth={3}
                dot={false}
                name="Revenue"
              />
              <Line
                type="monotone"
                dataKey="pos"
                stroke="#16a34a"
                strokeWidth={2}
                dot={false}
                name="POS"
              />
              <Line
                type="monotone"
                dataKey="accommodation"
                stroke="#a855f7"
                strokeWidth={2}
                dot={false}
                name="Accommodation"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
